import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type PublicBoard } from '@air/api/types';

import { getPublicBoardPageKey } from '~/components/Layouts/PublicBoardLayout/hooks/usePublicBoardPage';
import { setCentralizedBoardIdAndTitleAction, setCentralizedPublicBoardAction } from '~/store/centralizedBoard/actions';

export type SeedCentralizedPublicBoardParams = {
  shortId: string;
  boardId: string;
  boardTitle: string;
};

/**
 * Seeds the centralized board for a just-drilled-into public board — the cached record when we
 * have it, id + title otherwise — so the board renders its title without waiting for the fetch.
 *
 * Consumed by BOTH PublicAssetModalNavigation implementations (the URL provider and the editor
 * canvas's bindings): the seed is the one genuinely surface-independent piece of a board drill,
 * which is why it lives here instead of being duplicated per implementation. It requires the redux
 * and react-query providers ABOVE the navigation provider — the surfaces mount them in that order
 * on purpose.
 */
export const useSeedCentralizedPublicBoard = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const seedCentralizedPublicBoard = useCallback(
    ({ shortId, boardId, boardTitle }: SeedCentralizedPublicBoardParams) => {
      const cachedBoard = queryClient.getQueryData<PublicBoard>(getPublicBoardPageKey({ shortId, boardId }));

      if (cachedBoard) {
        dispatch(setCentralizedPublicBoardAction({ board: cachedBoard }));
      } else {
        dispatch(setCentralizedBoardIdAndTitleAction({ board: { id: boardId, title: boardTitle } }));
      }
    },
    [dispatch, queryClient],
  );

  return {
    seedCentralizedPublicBoard,
  };
};
